import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchRecommendedProducts,
  type RecommendedProductsError,
  type RecommendedProductsResponse,
} from "src/services/CampaignAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { normalizeApiDetail } from "src/utils/showApiDetailToast";

const NOT_FOUND_STATUS = 404;
const BEST_SELLERS_STRATEGY = "best_sellers";
const NO_MATCHED_CUSTOMERS_REASON = "no_matched_customers";

export type RecommendedProductsStatus =
  | "idle"
  | "loading"
  | "ready"
  | "empty"
  | "unavailable"
  | "error";

interface UseRecommendedProductsParams {
  audienceNames: string[];
  excludeAudienceNames: string[];
  campaignName?: string;
  enabled: boolean;
}

export const useRecommendedProducts = ({
  audienceNames,
  excludeAudienceNames,
  campaignName,
  enabled,
}: UseRecommendedProductsParams) => {
  const { i18n } = useTranslation();
  const [response, setResponse] = useState<RecommendedProductsResponse | null>(null);
  const [status, setStatus] = useState<RecommendedProductsStatus>("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const requestIdRef = useRef(0);
  const loadedRequestKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(false);

  const audienceKey = JSON.stringify(audienceNames);
  const excludeKey = JSON.stringify(excludeAudienceNames);
  const trimmedCampaignName = campaignName?.trim() || undefined;
  const requestKey = [audienceKey, excludeKey, trimmedCampaignName, i18n.language].join("|");

  const isCurrentRequest = useCallback(
    (requestId: number) => isMountedRef.current && requestId === requestIdRef.current,
    []
  );

  const loadProducts = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const audience: string[] = JSON.parse(audienceKey);
    const excluded: string[] = JSON.parse(excludeKey);
    if (!audience.length) {
      setResponse(null);
      setStatus("empty");
      setStatusMessage("");
      return;
    }

    setStatus("loading");
    setStatusMessage("");
    try {
      const data = await fetchRecommendedProducts(
        {
          audience_names: audience,
          exclude_audience_names: excluded.length ? excluded : undefined,
          campaign_name: trimmedCampaignName,
        },
        getApiLangFromI18n(i18n)
      );
      if (!isCurrentRequest(requestId)) {
        return;
      }
      const hasProducts = Array.isArray(data?.products) && data.products.length > 0;
      setResponse(hasProducts ? data : null);
      setStatus(hasProducts ? "ready" : "empty");
      setStatusMessage(hasProducts ? "" : data?.message ?? "");
    } catch (error) {
      if (!isCurrentRequest(requestId)) {
        return;
      }
      const { status: httpStatus, detail } = (error ?? {}) as RecommendedProductsError;
      setResponse(null);
      setStatus(httpStatus === NOT_FOUND_STATUS ? "unavailable" : "error");
      setStatusMessage(normalizeApiDetail(detail));
    }
  }, [audienceKey, excludeKey, i18n, isCurrentRequest, trimmedCampaignName]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || loadedRequestKeyRef.current === requestKey) {
      return;
    }
    loadedRequestKeyRef.current = requestKey;
    loadProducts();
  }, [enabled, loadProducts, requestKey]);

  return useMemo(
    () => ({
      status,
      statusMessage,
      products: response?.products ?? [],
      audience: response?.audience ?? null,
      isBestSellers: response?.strategy === BEST_SELLERS_STRATEGY,
      hasNoMatchedCustomers: response?.fallback_reason === NO_MATCHED_CUSTOMERS_REASON,
      gridRows: response?.unlayer_rows?.grid_3 ?? [],
      reload: loadProducts,
    }),
    [loadProducts, response, status, statusMessage]
  );
};

export type RecommendedProductsState = ReturnType<typeof useRecommendedProducts>;
